import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Typography } from "@mui/material";
import { useChatContext } from "../../chat";
import { MARKDOWN_ALLOWED_ELEMENTS } from "../consts";
import { preprocessMarkdown } from "../utils";

export type MarkdownRendererProps = {
  text: string;
  fontSize?: any; // pass MUI responsive font size token
  color?: string;
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  text,
  fontSize,
  color,
}) => {
  const { handleSend } = useChatContext();
  const normalized = preprocessMarkdown(text);

  // Identify if there's an "Explore further" section and extract it
  const followUpMatch = normalized.match(/###\s*(Explore further|Follow-up questions)[\s\S]*?(?=\n###|\n##|\n#|$)/i);
  const followUpSection = followUpMatch ? followUpMatch[0] : '';

  const handleListItemClick = (content: string) => {
    // Extract the question text from the list item
    const questionText = content.trim();
    // Send the question as both frontend and backend prompts
    handleSend(false, questionText, questionText);
  };

  // Helper to check if a node is within the follow-up questions section
  const isNodeInFollowUpSection = (node: any): boolean => {
    if (!followUpSection) return false;

    // Check if this list item's content appears in the follow-up section
    // We'll use the node's position in the document
    try {
      if (node?.position?.start?.offset !== undefined) {
        const followUpStartIndex = normalized.search(/###\s*(Explore further|Follow-up questions)/i);
        if (followUpStartIndex === -1) return false;

        const followUpEndIndex = followUpStartIndex + followUpSection.length;
        return node.position.start.offset >= followUpStartIndex &&
               node.position.start.offset < followUpEndIndex;
      }
    } catch (e) {
      // Fallback: just check if we have follow-up section
    }
    return false;
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      // disallow HTML for safety
      skipHtml
      allowedElements={MARKDOWN_ALLOWED_ELEMENTS}
      components={{
        p: ({ node, ...props }) => (
          <Typography
            component="p"
            variant="body1"
            sx={{ fontSize: fontSize, whiteSpace: "pre-wrap", color: color, margin: 0, marginBottom: '16px' }}
            {...(props as any)}
          />
        ),
        h1: ({ node, ...props }) => (
          <Typography
            component="h1"
            variant="h4"
            sx={{ color: color, marginTop: '24px', marginBottom: '12px', fontWeight: 600 }}
            {...(props as any)}
          />
        ),
        h2: ({ node, ...props }) => (
          <Typography
            component="h2"
            variant="h5"
            sx={{ color: color, marginTop: '24px', marginBottom: '12px', fontWeight: 600 }}
            {...(props as any)}
          />
        ),
        h3: ({ node, children, ...props }) => (
          <Typography
            component="h3"
            variant="h6"
            sx={{ color: color, marginTop: '20px', marginBottom: '12px', fontWeight: 600 }}
            {...(props as any)}
          >
            {children}
          </Typography>
        ),
        h4: ({ node, ...props }) => (
          <Typography
            component="h4"
            variant="subtitle1"
            sx={{ color: color, marginTop: '16px', marginBottom: '8px', fontWeight: 600 }}
            {...(props as any)}
          />
        ),
        h5: ({ node, ...props }) => (
          <Typography
            component="h5"
            variant="subtitle2"
            sx={{ color: color, marginTop: '12px', marginBottom: '8px', fontWeight: 600 }}
            {...(props as any)}
          />
        ),
        h6: ({ node, ...props }) => (
          <Typography
            component="h6"
            variant="subtitle2"
            sx={{ color: color, marginTop: '12px', marginBottom: '8px', fontWeight: 500 }}
            {...(props as any)}
          />
        ),
        strong: ({ node, ...props }) => (
          <strong style={{ fontWeight: 600 }} {...props} />
        ),
        em: ({ node, ...props }) => <em {...props} />,
        ul: ({ node, ...props }) => {
          // Check if this is the list under "Explore further" section
          const isFollowUpList = isNodeInFollowUpSection(node);

          if (isFollowUpList) {
            // Use custom arrow symbol for follow-up questions
            return (
              <ul
                style={{
                  paddingLeft: "0.5rem",
                  marginTop: '8px',
                  marginBottom: '16px',
                  listStyleType: "none"
                }}
                {...props}
              />
            );
          }

          // Regular bullet list
          return (
            <ul
              style={{
                paddingLeft: "2.5rem",
                marginTop: '8px',
                marginBottom: '16px',
                listStyleType: "disc"
              }}
              {...props}
            />
          );
        },
        ol: ({ node, ...props }) => (
          <ol style={{ paddingLeft: "2.5rem", marginTop: '8px', marginBottom: '16px' }} {...props} />
        ),
        li: ({ node, children, ...props }) => {
          // Check if this list item is within the follow-up questions section
          const isInFollowUp = isNodeInFollowUpSection(node);

          if (isInFollowUp) {
            const textContent = typeof children === 'string' ? children :
              (children as any[])?.map((child: any) =>
                typeof child === 'string' ? child : child?.props?.children || ''
              ).join('') || '';

            return (
              <li
                style={{
                  marginBottom: 8,
                  lineHeight: 1.6,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  paddingLeft: '1.5rem',
                }}
                onClick={() => handleListItemClick(textContent)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                  e.currentTarget.style.opacity = '1';
                }}
                {...props}
              >
                <span style={{ position: 'absolute', left: 0 }}>⤷</span>
                {children}
              </li>
            );
          }
          // Regular non-clickable list item
          return <li style={{ marginBottom: 8, lineHeight: 1.6 }} {...props}>{children}</li>;
        },
        a: ({ node, ...props }) => (
          <a
            style={{ color: "#1976d2", textDecoration: "none" }}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          />
        ),
        br: () => <br />,
      }}
    >
      {normalized}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
